import { createStore, applyMiddleware } from "redux";
import { thunk } from "redux-thunk";
import { persistStore, persistReducer, createMigrate, createTransform } from "redux-persist";
import storage from "redux-persist/lib/storage";

import rootReducer from "reducers";
import migrations, { currentVersion } from "migrations";

/** Supervisor ticket lists must not rehydrate: stale rows overwrite queue-scoped fetches on reload. */
const supervisorNonPersistentTickets = createTransform(
    (inboundState: unknown) => {
        if (!inboundState || typeof inboundState !== "object") return inboundState;
        const s = inboundState as Record<string, unknown>;
        return {
            ...s,
            tickets: [],
            totalCount: 0,
            selectedTicket: null,
        };
    },
    (outboundState: unknown) => {
        if (!outboundState || typeof outboundState !== "object") return outboundState;
        const s = outboundState as Record<string, unknown>;
        return {
            ...s,
            tickets: [],
            totalCount: 0,
            selectedTicket: null,
            isLoading: false,
        };
    },
    { whitelist: ["supervisor"] }
);

/** Customer-scoped lists must not survive tab close / rehydrate (stale cross-customer data). */
const integrationNonPersistentSlice = createTransform(
    (inboundState: unknown) => {
        if (!inboundState || typeof inboundState !== "object") return inboundState;
        const s = inboundState as Record<string, unknown>;
        return {
            ...s,
            applications: [],
            applicationActions: [],
            baselineActions: [],
        };
    },
    (outboundState: unknown) => {
        if (!outboundState || typeof outboundState !== "object") return outboundState;
        const s = outboundState as Record<string, unknown>;
        return {
            ...s,
            applications: [],
            applicationActions: [],
            baselineActions: [],
        };
    },
    { whitelist: ["integration"] }
);

const persistConfig = {
    key: "root",
    storage,
    version: currentVersion,
    migrate: createMigrate(migrations, { debug: false }),
    // chat: don't persist (fresh fetch). auth: tokens live in memory + sessionStorage only.
    blacklist: ["chat", "auth"],
    transforms: [supervisorNonPersistentTickets, integrationNonPersistentSlice],
};

const persistedReducer = () => persistReducer(persistConfig, rootReducer);

export let store: any;
export default function getStore() {
    store = createStore(
        persistedReducer(),
        undefined,
        applyMiddleware(thunk)
    );
    const persistor = persistStore(store);

    return { store, persistor };
}

export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof store.getState>;
